<!--
  Reading direction, layout, and the stone's other photographs — in the **record header**.

  These are the **inscription's**, not this photograph's. Which way a text runs is a fact about
  the writing, so one answer covers every image of the stone — and the sibling images are listed
  right here, which is what made the old per-image storage so obviously wrong: you set a
  direction on one photograph while looking at a list of the others it did not apply to.

  They save immediately through `PATCH /api/inscriptions/by-id/{id}`, not through the box
  autosave. The bulk upsert's box list is authoritative, so it can no longer carry a direction
  at all.

  `reading-direction` is a whole task of its own with its own screen; this is the correction
  path, not the entry point.

  ── Why there is no disclosure any more *(moved 2026-08-18)* ───────────────────────────────
  This was its own `<details class="panel">` further down the rail, defaulting open and
  remembering the triangle in `hismaic.workspace.imagePanel`. That default existed to stop a
  shut disclosure from hiding the two controls most likely to be wrong on a record nobody has
  looked at — "a disclosure that starts shut is a disclosure most people never find".

  Sitting in the header, there is no disclosure to miss, so the storage key and the rule it
  served are both retired: the failure mode they guarded against cannot occur. What paid for
  the rail height is the prose — three paragraphs of reasoning that were printed inline are now
  behind `InfoTip` markers, available on demand rather than read past a hundred times a day.

  What has **not** changed, and must not: the values are stated whether or not anything is
  expanded, a failed write puts the control back and says so, and an image with no record
  disables both controls and gives the reason.
-->
<script lang="ts">
  import type { LayoutType, ReadingDirection } from '../lib/api/types';
  import { directionLabel } from '../lib/draft';
  import { href } from '../lib/router.svelte';
  import { session } from '../lib/stores/session.svelte';
  import InfoTip from './InfoTip.svelte';

  interface Props {
    /** The image being edited, so its own chip can be marked in the sibling list. */
    imageId: number;
  }

  const { imageId }: Props = $props();

  /**
   * `angle` is no longer offered.
   *
   * It is still a value the schema accepts and the service stores, so a record already set to
   * it keeps its answer and its degrees — the option reappears **only** for that record, so the
   * select never shows "not set" over a stored direction. It is not offered fresh anywhere else.
   */
  const DIRECTIONS: ReadingDirection[] = ['ltr', 'rtl', 'ttb', 'btt'];
  const LAYOUTS: LayoutType[] = ['linear', 'boustrophedon', 'spiral'];

  const siblingImages = $derived(session.inscription?.images ?? []);
  const linked = $derived(session.attributesWritable);
  const disabled = $derived(session.readOnly || !linked);
  const directions = $derived(
    session.readingDirection === 'angle' ? [...DIRECTIONS, 'angle' as const] : DIRECTIONS,
  );
</script>

<div class="inscription-attrs col">
  {#if !linked}
    <p class="small warn-text">
      This photograph matches no record, so there is no inscription to carry a reading direction.
      The catalog could not join it to a stone — it is an orphan, or its siglum is shared by two
      records — and these are facts about a stone.
    </p>
  {/if}

  <div class="row wrap fields">
    <label class="col small field">
      <span class="row label-row">
        Reading direction
        <InfoTip label="reading direction">
          Hismaic is multidirectional and letters mirror with the direction of writing. Setting
          this turns the reference forms in the chip strip and lays the strip out the record's
          way, and it is <strong>optional</strong> — box first, and set it when the boxes have
          shown you which way the text runs.
          <br /><br />
          <strong>It decides nothing a box stores.</strong> A box's handedness used to be
          defaulted from this field, which made "I judged this" and "I never looked" the same
          stored value; it is now recorded per box in the inspector, or not at all.
          <br /><br />
          This is the writing's direction, not the angle you are viewing at. Straightening the
          photograph is a view preference and does not set it.
        </InfoTip>
      </span>
      <select
        value={session.readingDirection ?? ''}
        {disabled}
        onchange={(event) =>
          session.setReadingDirection((event.currentTarget.value || null) as ReadingDirection | null)}
      >
        <option value="">not set</option>
        {#each directions as direction (direction)}
          <option value={direction}>{direction} — {directionLabel(direction)}</option>
        {/each}
      </select>
    </label>

    <label class="col small field">
      <span class="row label-row">
        Layout
        <InfoTip label="layout">
          <strong>Linear</strong> runs one way throughout. <strong>Boustrophedon</strong> turns at
          the end of each line and runs back the other way, like an ox ploughing — so alternate
          lines are mirrored. <strong>Spiral</strong> curves around, usually inward, and has no
          single direction at all.
          <br /><br />
          Both of the last two mean reading order is not raster order, which is why they are
          recorded rather than inferred: the auto-alignment step has to know before it can match
          predictions to the transliteration.
        </InfoTip>
      </span>
      <select
        value={session.layoutType ?? ''}
        {disabled}
        onchange={(event) =>
          session.setLayout((event.currentTarget.value || null) as LayoutType | null)}
      >
        <option value="">not set</option>
        {#each LAYOUTS as layout (layout)}
          <option value={layout}>{layout}</option>
        {/each}
      </select>
    </label>
  </div>

  {#if session.readingDirection === 'angle'}
    <label class="col small field angle">
      Baseline angle (degrees)
      <input
        type="number"
        value={session.readingDirectionAngle ?? 0}
        {disabled}
        onchange={(event) => session.setReadingDirection('angle', Number(event.currentTarget.value))}
      />
    </label>
  {/if}

  {#if session.attributeProblem}
    <p class="small warn-text" role="alert">
      <strong>Not saved.</strong>
      {session.attributeProblem.message} The controls above show what the service holds, not what
      you chose — try again.
    </p>
  {:else if session.attributeSaving}
    <p class="small muted">Saving…</p>
  {/if}

  {#if linked && siblingImages.length > 1}
    <div class="col small">
      <span class="row label-row muted">
        Other images of this inscription
        <InfoTip label="other images of this inscription">
          All {siblingImages.length} photographs of this stone share the reading direction and
          layout above. Setting either here settles it for every one of them, because it is a fact
          about the writing rather than about one photograph.
        </InfoTip>
      </span>
      <div class="row wrap">
        {#each siblingImages as image (image.id)}
          <a
            class="chiplink"
            class:current={image.id === imageId}
            href={href({ name: 'image', imageId: image.id })}
          >
            #{image.id} · {image.status}
          </a>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .inscription-attrs {
    gap: 6px;
    border-top: 1px solid var(--line);
    padding-top: 8px;
  }

  /* Side by side while there is room, stacked when there is not: the direction options carry
     their own gloss ("ttb — top to bottom") and get long. */
  .fields {
    gap: 10px;
  }

  .field {
    flex: 1 1 150px;
    min-width: 0;
    gap: 2px;
  }

  .field.angle {
    flex: 0 1 180px;
  }

  .field select,
  .field input {
    width: 100%;
    min-width: 0;
  }

  /* `align-items: center` so the marker sits on the text's line rather than its box. */
  .label-row {
    gap: 5px;
    align-items: center;
  }

  .warn-text {
    color: var(--warn);
  }

  .chiplink {
    border: 1px solid var(--line-strong);
    border-radius: 5px;
    padding: 2px 7px;
    text-decoration: none;
    font-size: 0.8rem;
  }

  .chiplink.current {
    border-color: var(--accent);
    background: var(--accent-dim);
  }
</style>
